import {
  ConflictGroupDto,
  ConflictGroupFileDto,
  ConflictResolutionItemDto,
  ConflictWaitingForState,
} from "../models/ConflictGroup";
import {
  ConflictResolutionAction,
  defaultResolutionAction,
} from "../models/ConflictResolutionAction";
import { isContentMismatch, isPathCollision } from "../models/ConflictReason";
import { MigrationStatus } from "../models/MigrationStatus";

/** Conflict resolutions are submitted in batches so a large backlog never ships one oversized request. */
export const RESOLVE_BATCH_SIZE = 300;

/** True when the group has a SharePoint copy to adopt (a migrated occupant or an in-group content mismatch), so AcceptSharePoint needs no winner. */
export const groupHasOccupant = (group: ConflictGroupDto): boolean =>
  group.hasMigratedOccupant ||
  group.files.some((f) => f.conflictReason != null && isContentMismatch(f.conflictReason));

/** The action a file will resolve with: an explicit override, else its prior resolution, else the default for its reason. */
export const resolvedActionFor = (
  file: ConflictGroupFileDto,
  overrides: Record<string, ConflictResolutionAction> = {}
): ConflictResolutionAction =>
  overrides[file.fileId] ?? file.conflictResolution ?? defaultResolutionAction(file.conflictReason);

/** A file that will not upload again (Finished, Not Found, Skipped Duplicate), so a decision on it changes nothing in SharePoint. */
export const isTerminalFile = (file: ConflictGroupFileDto): boolean =>
  file.status === MigrationStatus.Finished ||
  file.status === MigrationStatus.NotFound ||
  file.status === MigrationStatus.SkippedDuplicate;

/**
 * A file decided "Accept SharePoint copy" in a group with no SharePoint copy to adopt, so only a Replace from disk
 * winner gives it something to take over. With no winner left, groupResolveState marks the group missingWinner and the
 * card will not send it as it stands: the file needs a new winner or another action.
 */
export const acceptNeedsAWinner = (group: ConflictGroupDto, file: ConflictGroupFileDto): boolean =>
  file.conflictResolution === ConflictResolutionAction.AcceptSharePoint && !groupHasOccupant(group);

/**
 * A copy still held for a winner whose upload will not release it: the winner is in SharePoint, not found, no longer
 * the winner, or deleted. Sending the copy again with its current decision has the server re-check the group, which
 * releases it once the group has no winner left to upload. An accepting copy whose group has nothing else to adopt
 * (acceptNeedsAWinner) never gets that far: groupResolveState marks the group missingWinner, so the card does not send
 * it until another file is the winner or the copy has another action.
 */
export const isHeldForAWinnerThatWillNotLand = (file: ConflictGroupFileDto): boolean =>
  file.waitingForState != null && file.waitingForState !== ConflictWaitingForState.ToUpload;

/**
 * Whether the file is the group's winner: Replace from disk on a file that can still upload. The server counts a
 * group's winners the same way, so a finished former winner does not stop another file from becoming the winner.
 */
export const isWinner = (
  file: ConflictGroupFileDto,
  overrides: Record<string, ConflictResolutionAction> = {}
): boolean =>
  !isTerminalFile(file) && resolvedActionFor(file, overrides) === ConflictResolutionAction.ReplaceFromDisk;

/**
 * A winner the overrides take away: its recorded decision is Replace from disk and it can still upload. The server
 * keeps counting it as a winner until its new decision arrives, so that decision has to travel with any new winner.
 */
export const isDisplacedWinner = (
  file: ConflictGroupFileDto,
  overrides: Record<string, ConflictResolutionAction> = {}
): boolean =>
  !isTerminalFile(file) &&
  file.conflictResolution === ConflictResolutionAction.ReplaceFromDisk &&
  resolvedActionFor(file, overrides) !== ConflictResolutionAction.ReplaceFromDisk;

export interface GroupResolveState {
  winners: number;
  missingWinner: boolean;
  invalid: boolean;
}

/**
 * Winner count + validity for a group under the given per-file action overrides. Only files that can still upload
 * count, as on the server: a finished file's decision neither makes it a winner nor leaves it waiting for one.
 */
export const groupResolveState = (
  group: ConflictGroupDto,
  overrides: Record<string, ConflictResolutionAction> = {}
): GroupResolveState => {
  const winners = group.files.filter((f) => isWinner(f, overrides)).length;
  const hasOccupant = groupHasOccupant(group);
  // An AcceptSharePoint loser on a path collision with no occupant must adopt a ReplaceFromDisk winner.
  const acceptLoserNeedsWinner =
    !hasOccupant &&
    group.files.some((f) => {
      const a = resolvedActionFor(f, overrides);
      return (
        !isTerminalFile(f) &&
        a === ConflictResolutionAction.AcceptSharePoint &&
        f.conflictReason != null &&
        isPathCollision(f.conflictReason)
      );
    });
  const missingWinner = acceptLoserNeedsWinner && winners === 0;
  return { winners, missingWinner, invalid: winners > 1 || missingWinner };
};

/**
 * Split eligible groups into ResolveConflictGroup payloads. The server checks each group a request touches on its
 * own and refuses the whole request when one fails, so a group with a ReplaceFromDisk winner (the decision it is
 * likeliest to refuse) submits alone and cannot take other groups' files down with it; every other group's files
 * batch together, chunked to `batchSize`.
 */
export const buildResolveBatches = (
  eligibleGroups: ConflictGroupDto[],
  overrides: Record<string, ConflictResolutionAction> = {},
  batchSize: number = RESOLVE_BATCH_SIZE
): ConflictResolutionItemDto[][] => {
  const batches: ConflictResolutionItemDto[][] = [];
  const noWinner: ConflictResolutionItemDto[] = [];
  for (const group of eligibleGroups) {
    const items = group.files.map((f) => ({
      fileId: f.fileId,
      action: resolvedActionFor(f, overrides),
    }));
    if (groupResolveState(group, overrides).winners === 1) batches.push(items);
    else noWinner.push(...items);
  }
  for (let i = 0; i < noWinner.length; i += batchSize)
    batches.push(noWinner.slice(i, i + batchSize));
  return batches;
};

/** Lowercased file extension (incl. the dot) derived from a local path, or "" when there is none. */
export const extensionOf = (filePath: string): string => {
  const base = filePath.split(/[\\/]/).pop() ?? filePath;
  const dot = base.lastIndexOf(".");
  return dot > 0 ? base.slice(dot).toLowerCase() : "";
};
